import React, { useState } from 'react';
import {
  Box,
  Container,
  Stack,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Collapse,
  Divider,
} from '@mui/material';
import {
  LinkedIn,
  GitHub,
  Language as WebIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Article as BlogIcon,
  MenuBook as DocsIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import SearchComponent from './SearchComponent';

const UpperNavigation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileExpanded, setMobileExpanded] = useState(false);


  const handleMobileToggle = () => {
    setMobileExpanded(!mobileExpanded);
  };

  const quickLinks = [
    { label: 'Download', to: '/download', icon: <DownloadIcon sx={{ fontSize: 16 }} /> },
    { label: 'Blog', to: '/blog', icon: <BlogIcon sx={{ fontSize: 16 }} /> },
    { label: 'Docs', to: '/docs', icon: <DocsIcon sx={{ fontSize: 16 }} /> },
    { label: 'Tools', to: '/tools', icon: <SecurityIcon sx={{ fontSize: 16 }} /> },
  ];

  const socialLinks = [
    { 
      label: 'LinkedIn', 
      url: 'https://www.linkedin.com/company/kief-studio/', 
      icon: <LinkedIn sx={{ fontSize: 18 }} />,
      color: '#0077b5'
    },
    { 
      label: 'X (Twitter)', 
      url: 'https://x.com/Kief_ma', 
      icon: <XIcon sx={{ fontSize: 18 }} />,
      color: '#000000'
    },
    { 
      label: 'GitHub', 
      url: 'https://github.com/KiefStudioMA/PWGen-Rust', 
      icon: <GitHub sx={{ fontSize: 18 }} />,
      color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000'
    },
    { 
      label: 'Website', 
      url: 'https://kief.studio', 
      icon: <WebIcon sx={{ fontSize: 18 }} />,
      color: theme.palette.accent?.main || '#00695c'
    },
  ];

  const contactInfo = [
    { label: 'Support', href: 'mailto:support@kief.studio', icon: <EmailIcon sx={{ fontSize: 16 }} /> },
    { label: 'Contact', to: '/contact', icon: <PhoneIcon sx={{ fontSize: 16 }} /> },
  ];

  if (isMobile) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' 
            ? alpha(theme.palette.background.paper, 0.8)
            : alpha(theme.palette.grey[50], 0.95),
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="center" sx={{ py: 1 }}>
            {/* Mobile Menu Toggle */}
            <Button
              onClick={handleMobileToggle}
              size="small"
              endIcon={mobileExpanded ? <CollapseIcon /> : <ExpandIcon />}
              sx={{ fontSize: '0.75rem' }}
            >
              Quick Links
            </Button>
          </Stack>


          {/* Expanded Mobile Menu */}
          <Collapse in={mobileExpanded}>
            <Box sx={{ pb: 2 }}>
              <Stack spacing={2}>
                {/* Quick Links */}
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                  {quickLinks.map((link) => (
                    <Button
                      key={link.label}
                      component={RouterLink}
                      to={link.to}
                      size="small"
                      startIcon={link.icon}
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                    >
                      {link.label}
                    </Button>
                  ))}
                </Stack>

                <Divider />

                {/* Social Links */}
                <Stack direction="row" spacing={1} justifyContent="center">
                  {socialLinks.map((social) => (
                    <IconButton
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: social.color,
                        '&:hover': {
                          backgroundColor: alpha(social.color, 0.1),
                        }
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Stack>
              </Stack>
            </Box>
          </Collapse>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.background.paper, 0.8)
          : alpha(theme.palette.grey[50], 0.95),
        borderBottom: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Container maxWidth="xl">
        <Stack 
          direction="row" 
          alignItems="center" 
          justifyContent="space-between" 
          sx={{ py: 1.5 }}
        >
          {/* Left Side - Quick Links */}
          <Stack direction="row" spacing={2} alignItems="center">
            {quickLinks.map((link, index) => (
              <React.Fragment key={link.label}>
                <Button
                  component={RouterLink}
                  to={link.to}
                  size="small"
                  startIcon={link.icon}
                  sx={{
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    '&:hover': {
                      color: theme.palette.accent?.main || '#00695c',
                      backgroundColor: alpha(theme.palette.accent?.main || '#00695c', 0.05),
                    },
                    minWidth: 'auto',
                    px: 1.5,
                  }}
                >
                  {link.label}
                </Button>
                {index < quickLinks.length - 1 && (
                  <Box sx={{ width: 1, height: 16, backgroundColor: 'divider' }} />
                )}
              </React.Fragment>
            ))}
          </Stack>

          {/* Center - Search */}
          <Box sx={{ flexGrow: 1, maxWidth: 400, mx: 3 }}>
            <SearchComponent 
              variant="compact" 
              placeholder="Search PWGen-Rust..."
              showFilters={false}
              enableSuggestions={false}
            />
          </Box>

          {/* Right Side - Social & Contact */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Contact Info (only on larger screens) */}
            {!isTablet && (
              <>
                {contactInfo.map((contact, index) => (
                  <React.Fragment key={contact.label}>
                    <Button
                      component={contact.href ? 'a' : RouterLink}
                      href={contact.href}
                      to={contact.to}
                      size="small"
                      startIcon={contact.icon}
                      sx={{
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        '&:hover': {
                          color: theme.palette.accent?.main || '#00695c',
                        },
                        minWidth: 'auto',
                        px: 1,
                      }}
                    >
                      {contact.label}
                    </Button>
                    {index < contactInfo.length - 1 && (
                      <Box sx={{ width: 1, height: 16, backgroundColor: 'divider' }} />
                    )}
                  </React.Fragment>
                ))}
                <Box sx={{ width: 1, height: 20, backgroundColor: 'divider', mx: 1 }} />
              </>
            )}

            {/* Social Links */}
            {socialLinks.map((social) => (
              <IconButton
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: social.color,
                    backgroundColor: alpha(social.color, 0.1),
                  }
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default UpperNavigation;